import { AwaitStore, Observable } from "@hibas123/utils";
import { Fragment, h } from "preact";
import { Home, Settings } from "preact-feather";
import { useEffect, useMemo, useState } from "preact/hooks";
import Api, { FullWork, Position } from "../../api";
import { getLogging } from "../../logging";
import Navigation, { Pages } from "../../navigation";
import "./reader.scss";
import Shadow from "./shadow";

const ReaderLogging = getLogging("reader");

const color_themes = {
   white: {
      background: "#FFFFFF",
      color: "#000000"
   },
   black: {
      background: "#000000",
      color: "#FFFFFF"
   },
   sepia: {
      background: "#e9d8ba",
      color: "#412811"
   }
};

const fonts = {
   "Open Sans": "'Open Sans', sans-serif",
   Roboto: "'Roboto', sans-serif",
   "Roboto Mono": " 'Roboto Mono', monospace",
   Oswald: " 'Oswald', sans-serif",
   "Liu Jian Mao Cao": "'Liu Jian Mao Cao', cursive",
   Anton: "'Anton', sans-serif"
};

// const fontSizes = {
//    small: "12px",
//    medium: "1rem",
//    large: "20px",
//    XL: "22px",
//    XXL: "26px"
// };

interface ITheme {
   color_theme: string;

   font: string;
   font_size: number;
   padding: number;
}

const between = (x: number, min: number, max: number) => x >= min && x <= max;

const LS_THEME_COLOR = "reader-color_theme";
const LS_THEME_FONT = "reader-font";
const LS_THEME_FONT_SIZE = "reader-font_size";
const LS_THEME_PADDING = "reader-padding";

const ThemeStore = new AwaitStore({
   color_theme: localStorage.getItem(LS_THEME_COLOR) || "white",
   font: localStorage.getItem(LS_THEME_FONT) || fonts.Roboto,
   font_size: Number(localStorage.getItem(LS_THEME_FONT_SIZE)) || 16,
   padding: Number(localStorage.getItem(LS_THEME_PADDING)) ?? 32
} as ITheme);

ThemeStore.subscribe(theme => {
   if (theme) {
      // Store Theme into Local Storage on change!
      localStorage.setItem(LS_THEME_COLOR, theme.color_theme);
      localStorage.setItem(LS_THEME_FONT, theme.font);
      localStorage.setItem(LS_THEME_FONT_SIZE, theme.font_size.toString());
      localStorage.setItem(LS_THEME_PADDING, theme.padding.toString());
   }
});

const generateColorStyles = (theme: ITheme) => {
   let ct = color_themes[theme.color_theme];
   return `;background-color: ${ct.background};color:${ct.color};`;
};

const generateAllStyles = (theme: ITheme) => {
   return (
      generateColorStyles(theme) +
      `font-family: ${fonts[theme.font]}; font-size: ${theme.font_size
      }px; padding: ${theme.padding}px;`
   );
};

interface IThemeHookRes {
   theme: ITheme;
   styles_color: string;
   styles_all: string;
}
function useReaderTheme(): [IThemeHookRes, (theme: ITheme) => void] {
   const [theme, setTheme] = useState(ThemeStore.value);

   useEffect(() => {
      let s = ThemeStore.subscribe(value => setTheme(value));
      return () => s();
   }, []);

   let res = {
      theme,
      styles_color: generateColorStyles(theme),
      styles_all: generateAllStyles(theme)
   };

   return [res, (theme: ITheme) => ThemeStore.send(theme)];
}

interface IThemeModalProps {
   work: FullWork;
   chapterID: string;
   changeChapter: (newChap: string) => void;
   close: () => void;
}

function ReaderModal({
   work,
   chapterID,
   changeChapter,
   close
}: IThemeModalProps) {
   const [theme, setTheme] = useReaderTheme();

   return (
      <div
         class="modal"
         style={"border: 1px white solid;" + theme.styles_color}
      >
         <div class="modal-content">
            <div class="input-group">
               <label>Chapter</label>
               <select
                  class="inp"
                  style={theme.styles_color}
                  onChange={evt => {
                     let elm = evt.srcElement as HTMLSelectElement;
                     let value = elm.options[elm.selectedIndex].value;
                     let chapter = work.chapters.find(e => e.id === value);
                     if (chapter) {
                        changeChapter(chapter.id);
                     }
                  }}
               >
                  {work.chapters.map(chapter => (
                     <option
                        value={chapter.id}
                        key={chapter.id}
                        selected={chapter.id === chapterID}
                     >
                        {chapter.title}
                     </option>
                  ))}
               </select>
            </div>
            <div class="input-group">
               <label>Color Theme</label>
               <select
                  class="inp"
                  style={theme.styles_color}
                  onChange={evt => {
                     let elm = evt.srcElement as HTMLSelectElement;
                     let value = elm.value || "white";

                     setTheme({
                        ...theme.theme,
                        color_theme: value
                     });
                  }}
               >
                  {Object.keys(color_themes).map(name => (
                     <option
                        value={name}
                        key={name}
                        selected={name === theme.theme.color_theme}
                     >
                        {name}
                     </option>
                  ))}
               </select>
            </div>
            <div class="input-group">
               <label>Font</label>
               <select
                  class="inp"
                  style={theme.styles_color}
                  onChange={evt => {
                     let elm = evt.srcElement as HTMLSelectElement;
                     let value = elm.value || "white";

                     setTheme({
                        ...theme.theme,
                        font: value
                     });
                  }}
               >
                  {Object.keys(fonts).map(name => (
                     <option
                        value={name}
                        key={name}
                        selected={name === theme.theme.font}
                        style={"font-family:" + fonts[name] + ";"}
                     >
                        {name}
                     </option>
                  ))}
               </select>
            </div>
            <div class="input-group">
               <label>Font Size</label>
               <input
                  type="range"
                  style={theme.styles_color}
                  min="12"
                  max="64"
                  step="2"
                  value={theme.theme.font_size}
                  onChange={evt => {
                     let elm = evt.srcElement as HTMLSelectElement;
                     let value = Number(elm.value) || 16;

                     setTheme({
                        ...theme.theme,
                        font_size: value
                     });
                  }}
               />
            </div>

            <div class="input-group">
               <label>Padding</label>
               <input
                  type="range"
                  style={theme.styles_color}
                  min="0"
                  max="128"
                  step="8"
                  value={theme.theme.padding}
                  onChange={evt => {
                     let elm = evt.srcElement as HTMLSelectElement;
                     let value = Number(elm.value) ?? 32;

                     setTheme({
                        ...theme.theme,
                        padding: value
                     });
                  }}
               />
            </div>
         </div>
         <div className="modal-action">
            <button onClick={() => close()}>Close</button>
         </div>
      </div>
   );
}

interface IReaderOverlayProps {
   work: FullWork;
   chapterID: string;
   changeChapter: (id: string) => void;
   toggle: Observable;
}

function ReaderOverlay({
   work,
   chapterID,
   changeChapter,
   toggle
}: IReaderOverlayProps) {
   const [overlay, setOverlay] = useState(false);
   const [modalActive, setModalActive] = useState(false);

   useEffect(() => {
      ReaderLogging.log("ReaderOverlay subscribe toggle");
      const onToggle = () => {
         ReaderLogging.log("ReaderOverlay Changing visibility to", !overlay);
         if (overlay) {
            setOverlay(false);
            setModalActive(false);
         } else {
            setOverlay(true);
         }
      };

      toggle.subscribe(onToggle);
      return () => toggle.unsubscribe(onToggle);
   }, [toggle, overlay]);

   ReaderLogging.debug(
      "ReaderOverlay Visibility:",
      overlay,
      "Modal:",
      modalActive
   );

   return (
      <Fragment>
         <div
            class={
               "reader-overlay" + (overlay ? " reader-overlay-visible" : "")
            }
         >
            <div
               class="reader-settings-icon right"
               onClick={() => setModalActive(!modalActive)}
            >
               <Settings />
            </div>

            <div
               class="reader-settings-icon left"
               onClick={() => Navigation.setPage(Pages.Library, undefined)}
            >
               <Home />
            </div>
         </div>

         {modalActive && (
            <ReaderModal
               work={work}
               chapterID={chapterID}
               changeChapter={changeChapter}
               close={() => {
                  setModalActive(false);
                  setOverlay(false);
               }}
            />
         )}
      </Fragment>
   );
}

interface IChapterNavigationProps {
   prev?: () => void;
   next?: () => void;
}

function ChapterNavigation({ prev, next }: IChapterNavigationProps) {
   return (
      <div style="display:flex; margin: 16px;">
         <a
            id="reader_prev_chap"
            href="#"
            style={!prev ? "display:none" : ""}
            onClick={evt => {
               evt.preventDefault();
               prev();
            }}
         >
            Previous Chapter
         </a>
         <span style="flex-grow: 1"></span>
         <a
            id="reader_next_chap"
            href="#"
            style={!next ? "display:none" : ""}
            onClick={evt => {
               evt.preventDefault();
               next();
            }}
         >
            Next Chapter
         </a>
      </div>
   );
}

export default function Reader({ id }: { id: string }) {
   const [work, setWork] = useState<FullWork>(null);
   const [position, setPosition] = useState<Position>(null);
   const [chapterIdx, chapter] = useMemo(() => {
      if (work && position) {
         let idx = work.chapters.findIndex(e => e.id === position.chapter);

         return [idx, work.chapters[idx]];
      }
      return [];
   }, [work, position]);
   const [theme] = useReaderTheme();

   const overlayToggle = useMemo(() => new Observable(), []);

   useEffect(() => {
      Api.getWork(id).then(work => {
         if (!work.position)
            work.position = {
               chapter: work.chapters[0].id,
               date: new Date(),
               progress: 0
            };

         setPosition(work.position);
         setWork(work);
      });
   }, [id]);

   // End hooks

   const updatePosition = (chapter: string, progress: number) => {
      ReaderLogging.log("Updating positon to:", chapter, progress);
      let np = {
         date: new Date(),
         chapter: chapter ? chapter : position.chapter,
         progress: progress
      };

      if (chapter) {
         setPosition(np);
      }

      Api.pushPosition(work._id, np);
   };

   useEffect(() => {
      if (!chapter) return;
      let oldScroll = 0;

      setTimeout(() => {
         let pos = document.documentElement.scrollHeight * position.progress;
         ReaderLogging.debug("Scrolling to:", pos, position.progress);
         document.documentElement.scrollTo({ top: pos });
         this.oldScroll = pos;
      }, 100);

      const iv = setInterval(() => {
         let {
            scrollTop,
            scrollHeight,
            clientHeight
         } = document.documentElement;

         ReaderLogging.log(
            "Check position: ",
            scrollTop,
            scrollHeight,
            clientHeight
         );
         if (scrollTop + clientHeight + 20 >= scrollHeight) {
            //Is on bottom of page
            scrollTop = scrollTop + scrollHeight;
            if (scrollTop != oldScroll) {
               oldScroll = scrollTop;
               updatePosition(undefined, 1);
            }
         } else {
            if (!between(oldScroll, scrollTop - 50, scrollTop + 50)) {
               oldScroll = scrollTop;
               let c = scrollTop / scrollHeight;
               updatePosition(undefined, c);
            }
         }
      }, 3000);
      return () => clearInterval(iv);
   }, [chapter]);

   if (!work) return;

   const next =
      chapterIdx < work.chapters.length - 1
         ? () => updatePosition(work.chapters[chapterIdx + 1].id, 0)
         : undefined;

   const prev =
      chapterIdx > 0
         ? () => updatePosition(work.chapters[chapterIdx - 1].id, 0)
         : undefined;

   function toggleOverlay(evt: MouseEvent) {
      // console.log(evt.composedPath(), evt);
      const is = (elm: HTMLElement) =>
         elm.classList
            ? elm.classList.contains("modal") ||
            elm.classList.contains("reader-settings-icon")
            : false;
      if (evt.composedPath().find(e => is(e as HTMLElement))) {
         //Click on Modal or settings button
      } else {
         overlayToggle.send("asd");
      }
   }

   if (work) {
      return (
         <div
            class="reader-container"
            style={theme.styles_color}
            onClick={toggleOverlay}
         >
            <ReaderOverlay
               work={work}
               chapterID={chapter.id}
               changeChapter={id => updatePosition(id, 0)}
               toggle={overlayToggle}
            />
            <div className="row">
               <div class="col col-12 col-sm-12 col-md-8 col-md-offset-2 col-lg-8 col-lg-offset-2 col-xl-6 col-xl-offset-3">
                  <ChapterNavigation next={next} prev={prev} />
                  <Shadow>
                     <style>{`
.reader {
   overflow: hidden;
   word-wrap: break-word;
   word-break: break-word;
   user-select: none;
   max-width: 100%;
   // min-height: calc(100vh - var(--header-height));
}

.reader p {
   max-width: 100%;
}

.reader img {
   width: 100%;
}                     
                     `}</style>
                     <div className="reader" style={theme.styles_all}>
                        <h1>{chapter.title}</h1>
                        <div
                           dangerouslySetInnerHTML={{ __html: chapter.content }}
                        />
                     </div>
                  </Shadow>
                  <ChapterNavigation next={next} prev={prev} />
               </div>
            </div>
         </div>
      );
   }

   return null;
}
